import { Box, Collapse, Paper, Stack, SxProps, Theme, Typography } from '@mui/material';
import parse from 'html-react-parser';
import { useEffect, useMemo, useRef, useState } from 'react';
import { LandingSectionTitle } from './landing-section-title';
import { formatForTinyMCE } from '../utils';

export type CollapsableInfoProps = {
  title?: string;
  subtitle?: string;
  subtitleColor?: string;
  logoSrc?: string | undefined;
  logoLink?: string;
  text: string;
  collapsedLines?: number;
  lineHeight?: number;
  expandedLines?: number;
  readMoreText?: string;
  readLessText?: string;
  containerSx?: SxProps<Theme>;
};


export function CollapsableInfo({
  title,
  subtitle,
  subtitleColor,
  text,
  logoSrc,
  logoLink,
  collapsedLines = 8,
  lineHeight = 21,
  expandedLines = 100,
  readMoreText = 'Read more',
  readLessText = 'Read less',
  containerSx,
}: CollapsableInfoProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);
  const [forceUpdate, setForceUpdate] = useState(0);
  const formattedText = useMemo(() => formatForTinyMCE(text), [text]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const textLines = useMemo(
    () =>
      textRef.current?.scrollHeight ? Math.ceil(textRef.current.scrollHeight / lineHeight) : 0,
    [textRef.current?.scrollHeight, text, forceUpdate, lineHeight],
  );

  useEffect(() => {
    const handleResize = () => {
      setForceUpdate((prev) => prev + 1);
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const collapsedHeight = useMemo(
    () => Math.min(textLines * lineHeight, collapsedLines * lineHeight),
    [textLines, lineHeight, collapsedLines],
  );

  const showMoreBtn = useMemo(() => textLines > collapsedLines, [textLines, collapsedLines]);

  return (
    <Paper
      elevation={1}
      sx={{
        border: '1px solid #EAECF0',
        margin: { xs: '0 16px', sm: '0' },
        borderRadius: '12px',
      }}
    >
      <Stack
        padding={{ xs: '15px', sm: '20px' }}
        spacing={{ xs: '7px', sm: '10px' }}
        sx={containerSx}
      >
        <Stack direction="column" flexGrow={1} spacing={1}>
          <LandingSectionTitle title={title} />

          {subtitle && (
            <Typography
              color={subtitleColor || 'primary.main'}
              sx={{
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                whiteSpace: 'normal',
                '& p': { marginBlock: '0rem' },
                '& ul': { marginBlock: '0.25rem' },
              }}
              variant="h6"
              component="h3"
            >
              {parse(subtitle || '')}
            </Typography>
          )}

          {logoSrc && (
            <Box
              sx={{ height: { xs: '50px', sm: '70px' }, width: 'fit-content' }}
              component={logoLink ? 'a' : 'div'}
              href={logoLink}
              target={logoLink ? '_blank' : undefined}
              rel="noopener" // dont't set "noreferrer" - to share referrer info
            >
              <img
                src={`${logoSrc}${logoSrc?.includes('?v=') ? '' : `?v=${Date.now()}`}`}
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
            </Box>
          )}

          <Collapse in={isExpanded} collapsedSize={collapsedHeight}>
            <Typography
              ref={textRef}
              sx={{
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: isExpanded ? expandedLines : collapsedLines,
                overflow: 'hidden',
                visibility: textLines === 0 ? 'hidden' : 'visible',
                whiteSpace: 'normal',
                '& p': { marginBlock: '0rem' },
                '& ul': { marginBlock: '0.25rem' },
              }}
              component="div"
            >
              {parse(formattedText)}
            </Typography>
          </Collapse>
          {showMoreBtn && (
            <Typography
              variant="body2"
              fontWeight={500}
              style={{ whiteSpace: 'break-spaces' }}
              color="primary.main"
              onClick={() => setIsExpanded(!isExpanded)}
              data-treedistestid="button-expand-info"
              sx={{ cursor: 'pointer' }}
            >
              {!isExpanded ? readMoreText : readLessText}
            </Typography>
          )}
        </Stack>
      </Stack>
    </Paper>
  );
}
